











































import { Component, Mixins, Prop, Vue } from "vue-property-decorator";

@Component
export default class UPinInput extends Mixins(Vue) {
  // Props

  @Prop({ default: "" })
  public value!: string;

  @Prop({ default: 6 })
  public length!: number;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: true })
  public autoFocusNext!: boolean;

  @Prop({ default: true })
  public focusFirstAtInit!: boolean;

  @Prop({ default: 3 })
  public splitAtPosition!: number;

  @Prop({ default: true })
  public focusable!: boolean;

  @Prop({ default: "" })
  public emptyChar!: string;

  @Prop({ default: "0" })
  public minChar!: string;

  @Prop({ default: "9" })
  public maxChar!: string;

  @Prop({ default: "2rem" })
  public fontSize!: string;

  @Prop({ default: () => /^\d+$/ })
  public validationRegex!: RegExp;

  @Prop({ default: "off" })
  public autocomplete!: string;

  // Data

  public chars: { index: number; value: string }[] = [];
  public focusedIndex = -1;

  // Life-cycle methods

  public created() {
    this.$watch("value", newVal => {
      if (
        newVal &&
        newVal.length === this.length &&
        newVal !== this.currentValue
      )
        this.init();
    });
    this.init();
  }

  // Computed

  public get currentValue() {
    return this.chars.map(i => i.value).join("");
  }

  public get isValid() {
    return (
      this.chars.filter(i => i.value !== this.emptyChar).length === this.length
    );
  }

  // Methods

  public init() {
    this.reset();
    for (let index = 0; index < this.length; index++) {
      this.addCharObject(
        index,
        this.$_.get(this.value, `[${index}]`, this.emptyChar)
      );
    }

    if (this.focusFirstAtInit) this.setFocusedCharIndex(0);
  }

  public onChange({ index, value, next = true }) {
    if (this.isDisabled) return;
    if (!index && value.length > 1) return this.autofill(value);

    // Validate with regex
    const match = this.validationRegex.exec(value);
    if (this.$_.isEmpty(match)) {
      const charBeforeMinChar = String.fromCharCode(
        this.minChar.charCodeAt(0) - 1
      );

      const charAfterMaxChar = String.fromCharCode(
        this.maxChar.charCodeAt(0) + 1
      );

      // Loop values. If maxChar is 9 at UP arrow, go back to minChar (0)
      if (charBeforeMinChar === value) this.chars[index].value = this.maxChar;
      else if (charAfterMaxChar === value)
        this.chars[index].value = this.minChar;
      else this.chars[index].value = this.emptyChar;

      return (this.$refs.pins[index].value = this.chars[index].value);
    }

    this.$refs.pins[index].value = value;
    this.chars[index].value = value || this.emptyChar;
    if (this.autoFocusNext && next)
      this.setFocusedCharIndex(this.focusedIndex + 1);

    this.onInput(this.currentValue);
  }

  public addCharObject(index, value) {
    this.chars.push({ index, value });
  }

  public setFocusedCharIndex(newIndex) {
    if (!this.focusable || newIndex < 0 || newIndex >= this.length) {
      return;
    }
    this.focusedIndex = newIndex;
    this.$nextTick(() => {
      if (
        this.$refs.pins &&
        document.activeElement !== this.$refs.pins[newIndex]
      )
        this.$refs.pins[newIndex].focus();
    });
  }

  public reset() {
    this.chars = [];
  }

  public onDelete(index) {
    if (this.isDisabled) return;
    this.chars[index].value = this.emptyChar;
    this.$refs.pins[index].value = this.emptyChar;
    this.setFocusedCharIndex(this.focusedIndex - 1);
    this.onInput(this.currentValue);
  }

  public onPaste(e) {
    if (this.isDisabled) return;
    this.autofill(e.clipboardData.getData("text") || "");
  }

  private autofill(text: string) {
    // Handle non-digits and shorter or longer values
    const data = [
      ...Array.from(text.replace(/\D/g, "")),
      ...Array(this.length)
    ].slice(0, this.length);

    let lastIndex = NaN;

    // Add all chars and fill with empty char if needed
    for (const [index, value] of data.entries()) {
      if (value) lastIndex = index;
      this.onChange({
        index,
        value: value ?? this.emptyChar,
        next: false
      });
    }

    this.onInput(this.currentValue);
    this.setFocusedCharIndex(
      isNaN(lastIndex) ? 0 : Math.min(++lastIndex, this.length - 1)
    );
  }

  public onInput(value: string) {
    this.$emit("input", this.isValid ? value : "");
  }
}
